/* ────────────────────────  GenAI Services  ──────────────────────── */

import { AxiosResponse } from 'axios';
import { ApiResponse } from 'types';
import axios, { logAxiosError } from 'utils/axios';
import {
    AIVideoResult,
    CreateMerchantProps,
    DataAIProps,
    DataRegionalizationProps,
    DataSpellProps,
    EditMerchantProps,
    GenerateDataWithImageProps,
    GenerateImageWithDataProps,
    GenerationProProps,
    HandleIsActiveProps
} from './ai.types';
import { FacebookType, GoogleType, MicrosoftType, OAUTHType, SAMLType, Webhook } from 'types/chat-gpt';
import { getBase64Img } from 'utils/helpers';
import { EditDetailProductType } from 'types/components/shared';

/* ─────────────────────────  Config & AI  ───────────────────────── */

export const isActiveService = async (): Promise<AxiosResponse<ApiResponse<any>>> => {
    try {
        return await axios.get('/loolu/api/genAI/config');
    } catch (err) {
        logAxiosError(err, 'isActiveService');
        throw err;
    }
};

export const handleIsActiveService = async (params: HandleIsActiveProps): Promise<AxiosResponse<ApiResponse<any>>> => {
    try {
        return await axios.patch('/loolu/api/genAI/config', params);
    } catch (err) {
        logAxiosError(err, 'handleIsActiveService');
        throw err;
    }
};

export const getBalanceService = async (): Promise<AxiosResponse<ApiResponse<any>>> => {
    try {
        return await axios.get('/loolu/api/genAI/balance');
    } catch (err) {
        logAxiosError(err, 'getBalanceService');
        throw err;
    }
};

/* ────────────────────────  Merchant CRUD  ──────────────────────── */

export const editMerchantService = async (data: EditMerchantProps): Promise<AxiosResponse<ApiResponse<any>>> => {
    try {
        const { merchant: name, ...rest } = data;
        return await axios.patch('/loolu/api/merchants', { name, ...rest });
    } catch (err) {
        logAxiosError(err, 'editMerchantService');
        throw err;
    }
};

export const createMerchantService = async (data: CreateMerchantProps): Promise<AxiosResponse<ApiResponse<any>>> => {
    try {
        return await axios.post('/loolu/api/merchants', data);
    } catch (err) {
        logAxiosError(err, 'createMerchantService');
        throw err;
    }
};

/* ─────────────────────────  Webhooks  ───────────────────────── */

export const createEditWebhookService = async (webhook: Webhook[]): Promise<AxiosResponse<ApiResponse<any>>> => {
    try {
        return await axios.patch('/loolu/api/merchants/webhooks', { webhook });
    } catch (err) {
        logAxiosError(err, 'createWebhookService');
        throw err;
    }
};

export const getWebhooksService = async (): Promise<Webhook[]> => {
    try {
        const response = await axios.get<Webhook[]>('/loolu/api/merchants/webhooks');
        return response.data ?? [];
    } catch (err) {
        logAxiosError(err, 'getWebhooksService');
        throw err;
    }
};

/* ─────────────────────────  Billing  ───────────────────────── */

export const getBillingService = async (): Promise<AxiosResponse<ApiResponse<any>>> => {
    try {
        return await axios.get('/loolu/api/merchants/config', { params: { entity: 'BILLING' } });
    } catch (err) {
        logAxiosError(err, 'getBillingService');
        throw err;
    }
};

export const sendInvoiceService = async (email: string): Promise<AxiosResponse<ApiResponse<any>>> => {
    try {
        return await axios.get('/loolu/api/ddddddddddddddd', { params: { email } });
    } catch (err) {
        logAxiosError(err, 'sendInvoiceService');
        throw err;
    }
};

/* ───────────────────────  Login Methods  ─────────────────────── */

export const getLoginMethodsService = async (): Promise<AxiosResponse<ApiResponse<any>>> => {
    try {
        return await axios.get('/loolu/api/merchants/config', { params: { entity: 'LOGIN' } });
    } catch (err) {
        logAxiosError(err, 'getLoginMethodsService');
        throw err;
    }
};

export const editLoginMethodsService = async (
    method: SAMLType | GoogleType | FacebookType | OAUTHType | MicrosoftType
): Promise<AxiosResponse<ApiResponse<any>>> => {
    try {
        return await axios.patch('/loolu/api/merchants/config', method, {
            params: { entity: 'LOGIN' }
        });
    } catch (err) {
        logAxiosError(err, 'editLoginMethodsService');
        throw err;
    }
};

export const createLoginMethodsService = async (
    method: SAMLType | GoogleType | FacebookType | OAUTHType | MicrosoftType
): Promise<AxiosResponse<ApiResponse<any>>> => {
    try {
        return await axios.patch('/loolu/api/merchants/config', method, {
            params: { entity: 'LOGIN' }
        });
    } catch (err) {
        logAxiosError(err, 'createLoginMethodsService');
        throw err;
    }
};

/* ──────────────────  Terms, Conditions & Balance  ────────────────── */

export const getTermsService = async (): Promise<
    AxiosResponse<
        {
            data: any;
            name: string;
        }[]
    >
> => {
    try {
        const resp = await axios.get('/loolu/api/merchants/config', { params: { entity: 'CONDITIONS' } });
        return resp;
    } catch (err) {
        logAxiosError(err, 'getTermsService');
        throw err;
    }
};

export const patchConditionsService = async (conditions: string): Promise<AxiosResponse<ApiResponse<any>>> => {
    try {
        return await axios.patch('/loolu/api/merchants/conditions', { conditions });
    } catch (err) {
        logAxiosError(err, 'patchConditionsService');
        throw err;
    }
};

export const generateImageBanner = async (body: {
    image: File;
    base64Img?: string;
    themeText: string;
    promotionalText: string;
}): Promise<
    AxiosResponse<
        {
            id: string;
            image: string;
        }[]
    >
> => {
    try {
        const base64ImgString = body.base64Img ? body.base64Img : await getBase64Img(body.image);

        const pureBase64 = base64ImgString.split(',')[1];

        const bodyParams = {
            themeText: body.themeText,
            promotionalText: body.promotionalText,
            image: pureBase64
        };

        return await axios.post('/loolu/api/genAI/image/theamedImagePro', bodyParams);
    } catch (err) {
        logAxiosError(err, 'editLoginMethodsService');
        throw err;
    }
};

export const enhanceImageService = async ({
    image,
    base64Img
}: {
    image?: File;
    base64Img?: string;
}): Promise<
    AxiosResponse<
        {
            id: string;
            image: string;
        }[]
    >
> => {
    const base64ImgString: string = base64Img ? base64Img : await getBase64Img(image!);

    const pureBase64 = base64ImgString.split(',')[1];

    const bodyParams = {
        image: pureBase64
    };

    return await axios.post('/loolu/api/genAI/image/imageEnhancerPro', bodyParams);
};

export const productInteractionAIService = async ({
    image,
    base64Img
}: {
    image?: File;
    base64Img?: string;
}): Promise<
    AxiosResponse<
        {
            id: string;
            image: string;
        }[]
    >
> => {
    const base64ImgString: string = base64Img ? base64Img : await getBase64Img(image!);

    const pureBase64 = base64ImgString.split(',')[1];

    const bodyParams = {
        image: pureBase64
    };

    return await axios.post('/loolu/api/genAI/image/productIteractionPro', bodyParams);
};

export const textGeneratorAiService = async ({
    image,
    base64Img,
    productData,
    language = 'es',
    country = 'MX'
}: {
    image?: File;
    base64Img?: string;
    productData: Partial<EditDetailProductType>;
    language?: string;
    country?: string;
}) => {
    function cleanProductData<T extends Record<string, any>>(data: Partial<T>): Partial<T> {
        const cleaned: Partial<T> = {};

        Object.entries(data).forEach(([key, value]) => {
            const keyLower = key.toLowerCase();
            const isIdKey = keyLower.includes('id');
            const isEmpty = value === null || value === undefined;

            if (!isIdKey && !isEmpty) {
                cleaned[key as keyof T] = value;
            }
        });

        return cleaned;
    }

    const base64ImgString: string = base64Img ? base64Img : image ? await getBase64Img(image) : '';

    const pureBase64 = (base64ImgString ?? '').split(',')[1];

    const bodyParams = {
        image: pureBase64 ?? '',
        productData: cleanProductData(productData)
    };

    return await axios.post('/loolu/api/genAI/text/productDataPro', bodyParams, {
        params: {
            language,
            country
        }
    });
};

export const animationGeneratorAiService = async ({
    image,
    base64Img,
    productData,
    language = 'es',
    country = 'MX'
}: {
    image?: File;
    base64Img?: string;
    productData: Partial<EditDetailProductType>;
    language?: string;
    country?: string;
}): Promise<AxiosResponse<AIVideoResult[]>> => {
    const base64ImgString: string = base64Img ? base64Img : await getBase64Img(image!);

    const pureBase64 = (base64ImgString ?? '').split(',')[1];

    function cleanProductData<T extends Record<string, any>>(data: Partial<T>): Partial<T> {
        const cleaned: Partial<T> = {};

        Object.entries(data).forEach(([key, value]) => {
            const keyLower = key.toLowerCase();
            const isIdKey = keyLower.includes('id');
            const isEmpty = value === null || value === undefined;

            if (!isIdKey && !isEmpty) {
                cleaned[key as keyof T] = value;
            }
        });

        return cleaned;
    }

    const bodyParams = {
        image: pureBase64 ?? '',
        productData: cleanProductData(productData)
    };

    return await axios.post('/loolu/api/genAI/image/productAnimationPro', bodyParams, {
        params: {
            language,
            country
        }
    });
};

export const generationProService = async ({ formData }: GenerationProProps): Promise<AxiosResponse<{ image: string }>> => {
    try {
        return await axios.post('/loolu/api/genAI/image/generationPro', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    } catch (err) {
        logAxiosError(err, 'generationProService');
        throw err;
    }
};

export const sendMessageService = async (dataIA: DataAIProps): Promise<AxiosResponse<ApiResponse<any>>> => {
    try {
        const { data: dataMessage, request: solic, language } = dataIA;
        return await axios.post(`/loolu/api/genAI/text/${solic}?language=${language}`, dataMessage);
    } catch (err) {
        logAxiosError(err, 'sendMessageService');
        throw err;
    }
};

export const spellCorrectionCheckService = async (dataIA: DataSpellProps): Promise<AxiosResponse<ApiResponse<any>>> => {
    try {
        const { msg: dataMessage } = dataIA;
        return await axios.post('/loolu/api/genAI/text/spelling', { rawText: dataMessage });
    } catch (err) {
        logAxiosError(err, 'spellCorrectionCheckService');
        throw err;
    }
};

export const regionalizationCheckService = async (dataIA: DataRegionalizationProps): Promise<AxiosResponse<ApiResponse<any>>> => {
    try {
        const { msg: dataMessage, country } = dataIA;
        return await axios.post(`/loolu/api/genAI/text/translate?country=${country}`, { text: dataMessage });
    } catch (err) {
        logAxiosError(err, 'regionalizationCheckService');
        throw err;
    }
};

export const generateDataWithImagesService = async (dataIA: GenerateDataWithImageProps): Promise<AxiosResponse<ApiResponse<any>>> => {
    try {
        const { language, country, images: imageLink, requestData } = dataIA;
        return await axios.post(`/loolu/api/genAI/image?language=${language}&country=${country}`, {
            imageLink,
            requestData
        });
    } catch (err) {
        logAxiosError(err, 'generateDataWithImagesService');
        throw err;
    }
};

export const generateImagesService = async (dataIA: GenerateImageWithDataProps): Promise<AxiosResponse<ApiResponse<any>>> => {
    try {
        return await axios.post('/loolu/api/genAI/image/generation', dataIA);
    } catch (err) {
        logAxiosError(err, 'generateImagesService');
        throw err;
    }
};
